import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiBriefcase, FiMessageSquare, FiSend, FiCheck, FiAlertTriangle } = FiIcons;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });
  const [status, setStatus] = useState('idle');
  const [responseMessage, setResponseMessage] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setResponseMessage('');

    try {
      const response = await fetch("https://formspree.io/f/xjgbpwar", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setResponseMessage("Thank you! We've received your message and will get back to you soon.");
        setFormData({
          name: '',
          email: '',
          subject: 'general',
          message: ''
        });
      } else {
        const data = await response.json();
        if (data.errors) {
          throw new Error(data.errors.map(error => error.message).join(", "));
        } else {
          throw new Error('Oops! There was a problem submitting your form');
        }
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus('error');
      setResponseMessage(error.message || 'Something went wrong. Please email us directly at support@ninjacopia.com');
    }
  };

  const contactMethods = [
    {
      icon: FiMail,
      title: 'General Support',
      description: 'For technical questions, setup help, and general inquiries',
      contact: 'support@ninjacopia.com',
      responseTime: 'Response within 24 hours'
    },
    {
      icon: FiBriefcase,
      title: 'Vendor Inquiries',
      description: 'For NinjaTrader vendor licensing questions and partnerships',
      contact: 'vendors@ninjacopia.com',
      responseTime: 'Response within 48 hours'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title="Contact Support"
        description="Get in touch with the NinjaCopia team. Technical support, vendor inquiries, and general questions about our NinjaTrader copier."
        url="https://ninjacopia.com/#/contact"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900/20 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Get in Touch
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
            >
              Have questions about NinjaCopia? Need technical support? We're here to help you succeed with your trading operation.
            </motion.p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Methods */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Contact Methods</h2>
              <div className="space-y-8">
                {contactMethods.map((method, index) => (
                  <motion.div
                    key={method.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-4 shrink-0">
                        <SafeIcon icon={method.icon} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                          {method.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-3">
                          {method.description}
                        </p>
                        <div className="space-y-1">
                          <p className="text-blue-600 dark:text-blue-400 font-medium">
                            {method.contact}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-500">
                            {method.responseTime}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* NinjaTrader Vendor Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-12 bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg"
              >
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  NinjaTrader Vendor License Application
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  We are currently in the process of applying for official NinjaTrader vendor licensing. This ensures NinjaCopia meets the highest standards for security, reliability, and compliance.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Join our waitlist to receive updates on our vendor approval status and be among the first to access NinjaCopia when it becomes available.
                </p>
              </motion.div>
            </div>

            {/* Contact Form */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <SafeIcon icon={FiMessageSquare} className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3" />
                  Send us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Subject
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="technical">Technical Support</option>
                      <option value="sales">Sales Question</option>
                      <option value="vendor">Vendor Inquiry</option>
                      <option value="partnership">Partnership</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                      placeholder="Tell us how we can help you..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'loading' || status === 'success'}
                    className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center"
                  >
                    {status === 'loading' && (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    )}
                    {status === 'success' && (
                      <SafeIcon icon={FiCheck} className="w-5 h-5 mr-2" />
                    )}
                    {status === 'success' ? 'Message Sent!' : status === 'loading' ? 'Sending...' : 'Send Message'}
                    {status === 'idle' && <SafeIcon icon={FiSend} className="w-5 h-5 ml-2" />}
                  </button>

                  {responseMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`text-center text-sm p-3 rounded-lg ${status === 'success'
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                        : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                        }`}
                    >
                      {responseMessage}
                    </motion.div>
                  )}

                  {status === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center"
                    >
                      <div className="flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm">
                        <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mr-2" />
                        Having trouble? Email us directly at{' '}
                        <a href="mailto:support@ninjacopia.com" className="text-blue-600 dark:text-blue-400 hover:underline ml-1">
                          support@ninjacopia.com
                        </a>
                      </div>
                    </motion.div>
                  )}
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;